import React from "react";
import dayjs from "dayjs";
import { makeObservable, observable, action } from "mobx";
import { Props as DataGridProps } from "../components/DataGrid/DataGrid";
import { getTemplates } from "../api/Templates";
import { UtcDateFormatter } from "../utils/dateformat/dateFormat";
import { Cookies } from "react-cookie";
import ListReviewerBtn from "../pages/Templates/ListReviewerBtn";
import Avatar from "@mui/material/Avatar";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import { defaultAllCode } from "../pages/Templates/LanguageCode";
import { defaultAllCategory } from "../pages/Templates/CategoryCode";
import { getItem } from "../utils/nauth";
import { postHistory } from "../api/History";
import * as Assets from "../components/Icon/assets";

const cookies = new Cookies();
const getCookie = (item: string) => {
  return cookies.get(item);
};
const userEmail = getItem("email") || getItem("name") || "No name";

class TemplatesStore {
  constructor() {
    makeObservable(this, {
      application: observable,
      status: observable,
      startDate: observable,
      endDate: observable,
      searchText: observable,
      searchOption: observable,
      gridOption: observable,
      headerStatus: observable,
      language: observable,
      category: observable,
      mixability: observable,
      reference: observable,
      asset: observable,
      entertainment: observable,
      badge: observable,
    });
  }

  application = getCookie("application") ? getCookie("application") : "all";
  status = getCookie("status") ? getCookie("status") : "InReview";

  nowDate = dayjs().toDate();
  startDate = window.location.pathname.includes("userid")
    ? dayjs("2023/01/01", "yyyy/MM/dd")
    : getCookie("startDate")
    ? getCookie("startDate")
    : dayjs().subtract(1, "month").toDate();

  endDate: Date = window.location.pathname.includes("userid")
    ? dayjs().toDate()
    : getCookie("endDate")
    ? dayjs(getCookie("endDate"), "yyyy/MM/dd").toDate()
    : dayjs().toDate();

  searchText = "";
  searchOption = "templateId";
  headerStatus = localStorage.getItem("columns") || "";
  language = getCookie("language") ? getCookie("language") : defaultAllCode;
  category = getCookie("category") ? getCookie("category") : defaultAllCategory;
  mixability = 1;
  reference = 1;
  asset = 1;
  entertainment = 1;

  badge = "none";

  gridOption: DataGridProps = {
    columns: [
      {
        field: "index",
        headerName: "No",
        filterable: false,
        renderCell: (index) =>
          index.api.getRowIndex(index.row.id) +
          1 +
          this.gridOption.page * this.gridOption.rowsPerPage,
        width: 50,
        align: "center",
        hide: this.headerStatus.includes("index"),
      },
      {
        field: "thumbnail",
        headerName: "Thumbnail",
        width: 120,
        renderCell: (param) => (
          <div
            style={{ height: "100%", display: "flex", alignItems: "center" }}
          >
            <img
              style={{ height: "calc(100% - 5px)", maxWidth: "100px" }}
              src={param.row.thumbnail}
              alt=""
            />
          </div>
        ),
        hide: this.headerStatus.includes("thumbnail"),
      },
      {
        field: "description",
        headerName: "Description",
        width: 270,
        hide: this.headerStatus.includes("description"),
      },
      {
        field: "userId",
        headerName: "User ID",
        width: 120,
        hide: this.headerStatus.includes("userId"),
      },
      {
        field: "name",
        headerName: "User Nickname",
        width: 250,
        hide: this.headerStatus.includes("name"),
      },
      { field: "lang", headerName: "Lang", width: 75 },
      { field: "region", headerName: "Region", width: 90 },
      {
        field: "reviewStatus",
        headerName: "Status",
        width: 140,
        hide: this.headerStatus.includes("reviewer"),
      },
      {
        field: "reviewer",
        headerName: "Reviewer",
        width: 140,
        renderCell: (param) => {
          if (
            param.row.reviewStatus === "In Review" &&
            param.row.reviewer === ""
          ) {
            return (
              <div
                style={{
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <ListReviewerBtn template={param.row} />
              </div>
            );
          } else if (
            param.row.reviewStatus === "Published" &&
            param.row.reviewer === ""
          ) {
            return (
              <div
                style={{
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Avatar
                  alt="KineMaster"
                  src="https://resource.kinemaster.com/www/favicon.ico"
                />
              </div>
            );
          } else {
            return param.row.reviewer;
          }
        },
        hide: this.headerStatus.includes("reviewer"),
      },
      {
        field: "createdAt",
        headerName: "Uploaded Date",
        width: 240,
        hide: this.headerStatus.includes("createdAt"),
        renderHeader: (params) => {
          return (
            <div
              style={{
                fontWeight: "500",
                display: "flex",
                alignItems: "center",
              }}
            >
              {/* 특정 헤더를 커스텀 렌더링합니다. */}
              {params.colDef.headerName}

              {this.gridOption.sort === "desc" ? (
                <ArrowUpwardIcon
                  fontSize="small"
                  style={{ marginLeft: "5px" }}
                />
              ) : (
                <ArrowDownwardIcon
                  fontSize="small"
                  style={{ marginLeft: "5px" }}
                />
              )}
            </div>
          );
        },
      },
      {
        field: "link",
        headerName: "Link",
        width: 80,
        hide: this.headerStatus.includes("link"),
        renderCell: () => {
          return (
            <>
              <img
                src={Assets.NewWindows}
                alt="new window"
                style={{
                  width: 20,
                  height: 20,
                  cursor: "pointer",
                }}
              />
            </>
          );
        },
      },
    ],

    rows: [],
    totalCount: 0,
    loading: true,
    page: Number(getCookie("page")) ? Number(getCookie("page")) : 0,
    rowsPerPage: Number(getCookie("rowsPerPage"))
      ? Number(getCookie("rowsPerPage"))
      : 100,
    sort: getCookie("tSort") ? getCookie("tSort") : "desc",
  };

  setApplication = action((application: string) => {
    this.application = application;
  });

  setStatus = action((status: string) => {
    this.status = status;
  });

  setMixability = action((mixability: number) => {
    this.mixability = mixability;
  });

  setReference = action((reference: number) => {
    this.reference = reference;
  });

  setAsset = action((asset: number) => {
    this.asset = asset;
  });

  setEntertainment = action((entertainment: number) => {
    this.entertainment = entertainment;
  });

  setStartDate = action((startDate: Date) => {
    this.startDate = startDate;
  });

  setEndDate = action((endDate: Date) => {
    this.endDate = endDate;
  });

  setSearchText = action((searchText: string) => {
    if (searchText.slice(0, 1) !== " ") {
      this.searchText = searchText;
    }
  });

  setSearchOption = action((searchOption: string) => {
    this.searchOption = searchOption;
  });

  setGridOption = action((gridOption: any) => {
    this.gridOption = {
      ...this.gridOption,
      ...gridOption,
    };
  });
  setLanguageChange = action((value: any) => {
    this.language = value;
  });

  setCategoryChange = action((category: any) => {
    this.category = category;
  });

  setBadge = action((badge: any) => {
    this.badge = badge;
  });

  onPageSizeChange = action((params: any) => {
    window.scrollTo(0, 0);
    this.gridOption.rowsPerPage = params;
  });

  onPageChange = action((params: any) => {
    window.scrollTo(0, 0);
    this.gridOption.page = params;
  });

  get getGridStatus() {
    return {
      page: this.gridOption.page + 1,
      display: this.gridOption.rowsPerPage,
      startedAt: this.startDate,
      endedAt: this.endDate,
      reviewStatus: this.status !== "All" ? this.status : undefined,
      searchType: this.searchText !== "" ? this.searchOption : undefined,
      searchText: this.searchText !== "" ? this.searchText : undefined,
    };
  }

  setRows = action(async () => {
    if (this.searchOption === "reviewer") {
      if (!this.searchText.includes("@") || !this.searchText.includes(".")) {
        alert("Please enter the full name including the domain.");
        return;
      }
    }

    const requestParameter = {
      page: this.gridOption.page + 1,
      display: this.gridOption.rowsPerPage,
      startedAt: this.startDate,
      endedAt: this.endDate,
      reviewStatus: this.status !== "All" ? this.status : undefined,
      searchType: this.searchText !== "" ? this.searchOption : undefined,
      searchText:
        this.searchText.trim() !== "" ? this.searchText.trim() : undefined,
      sort: this.gridOption.sort,
      language: this.language.value,
      category: this.category.value,
      mixability: this.searchOption === "score" ? this.mixability : undefined,
      reference: this.searchOption === "score" ? this.reference : undefined,
      asset: this.searchOption === "score" ? this.asset : undefined,
      entertainment:
        this.searchOption === "score" ? this.entertainment : undefined,
      application: this.application !== "all" ? this.application : "",
    };
    this.searchText = this.searchText.trim();
    this.gridOption.loading = true;
    this.gridOption.rows = [];
    await getTemplates(requestParameter)
      .then((data) => {
        if (data.response?.status) {
          const item = {
            action: "Get List",
            status: "Error",
            error: `get List (${data.response ? data.response.status : ""})`,
          };
          setTimeout(
            () =>
              postHistory(userEmail, item).catch((error) => {
                console.log("start review err", error);
              }),
            100
          );

          alert(
            `An error occurred while loading the template list. \nCode(${data.response.status})`
          );
        }

        localStorage.removeItem("reviewList");
        this.setGridOption({
          totalCount: data.totalCount,
          rows: data.templates.map((info: any) => {
            const reviewer = info.reviewer.split("@");
            return {
              ...info,
              id: info.templateId,
              thumbnail: info.imagePath,
              description: info.translations[0].description,
              createdAt: UtcDateFormatter(info.createdAt),
              userId: info.author.sub,
              name: info.author.name,
              lang: info.metaLanguageCode,
              region:
                info.metaCountryCode.length >= 249
                  ? "All Country"
                  : info.metaCountryCode,
              reviewStatus:
                info.reviewStatus === "InReview"
                  ? "In Review"
                  : info.reviewStatus,
              reviewer: reviewer[0],
            };
          }),
        });
      })
      .catch((error) => {
        console.log(error);
        this.gridOption.totalCount = 0;
      })
      .finally(() => {
        this.gridOption.loading = false;
      });
  });

  searchHandler = action(async () => {
    // 검색어 없을시 모달을 띄울것인가?
    this.gridOption.page = 0;
    await this.setRows();
  });
}

export default TemplatesStore;
